<template>
    <div class="columns is-centered is-mobile" v-if="!initDone">
        <LoadingSpinner2 class="has-added-margin" />
    </div>
    <div v-else class="columns is-multiline">
        <div class="column is-12">
            <div class="buttons">
                <label class="button">
                    <span>{{ translate("page.admin_edit_event.choose_file") }}</span>
                    <input ref="eventFileInput" type="file" style="display: none" accept="application/pdf" @change="onFileChanged" />
                </label>
                <LoadingButton :is-loading="savingInProgress" :disabled="!isValidFileChosen" class="button is-primary" @click="uploadFile">
                    {{ translate("page.admin_edit_event.save_file") }}
                </LoadingButton>
            </div>

            <div class="field" :class="{ 'has-text-danger': !validFileSize }">
                <span class="has-text-weight-bold">{{ translate("page.admin_edit_event.chosen_file") }}</span>
                <span>{{ fileName }}</span>
                <span v-if="!validFileSize">{{ translate("page.admin_edit_event.max_size") }}</span>
            </div>

            <div class="field">
                <label class="label">{{ translate("page.admin_edit_event.description_of_file") }}</label>
                <div class="control">
                    <input class="input" type="text" v-model="description" maxlength="150" />
                </div>
            </div>
        </div>

        <div class="column is-12">
            <div class="has-top-border mb-5"></div>

            <h3 class="title is-4">{{ translate("page.admin_edit_event.your_files") }}</h3>

            <template v-if="eventFiles.length > 0">
                <div
                    class="box is-hoverable is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center"
                    v-for="eventFile in eventFiles"
                    :key="eventFile.id"
                >
                    <div>
                        <div class="is-size-5">{{ eventFile.name }}</div>
                        <div class="is-italic is-size-6" v-if="eventFile.description">{{ eventFile.description }}</div>
                        <div class="is-size-7">{{ convertToTimezone(eventFile.created, timezone) }}</div>
                    </div>

                    <div>
                        <LoadingButton
                            :is-loading="deleteInProgress === eventFile.id"
                            class="button is-rounded is-danger"
                            @click="deleteEventFile(eventFile.id)"
                        >
                            <span class="icon"><i class="fa fa-trash"></i></span>
                            <span>{{ translate("common.delete") }}</span>
                        </LoadingButton>
                    </div>
                </div>
            </template>
            <Notification v-else class="is-info has-text-centered">
                <span class="icon">
                    <i class="fa fa-info-circle" />
                </span>
                <span>{{ translate("page.admin_edit_event.no_files") }}</span>
            </Notification>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted } from "vue";
    import { useEditEventStore } from "../editEventStore";
    import eventsApi from "../../Api/events";
    import type { EventFile } from "../../Api/events";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import Notification from "../../Common/Notification.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";

    const store = useEditEventStore();

    const eventFileInput = ref<HTMLInputElement | null>(null);
    const file = ref<File | null>(null);
    const description = ref("");
    const eventFiles = ref<EventFile[]>([]);
    const savingInProgress = ref(false);
    const deleteInProgress = ref<number | false>(false);
    const initDone = ref(false);

    const timezone = computed(() => store.event.timezone);

    const fileName = computed(() => {
        return file.value?.name || "None";
    });

    const validFileSize = computed(() => {
        return file.value === null || file.value.size <= 5 * 1024 * 1024;
    });

    const isValidFileChosen = computed(() => {
        return file.value !== null && validFileSize.value;
    });

    const onFileChanged = (event: Event) => {
        const target = event.target as HTMLInputElement;
        if (target.files && target.files.length > 0) {
            file.value = target.files[0];
        }
    };

    const uploadFile = () => {
        if (!file.value) {
            return;
        }

        savingInProgress.value = true;
        eventsApi
            .uploadEventFile(store.eventId, description.value, file.value)
            .then(() => {
                file.value = null;
                description.value = "";

                if (eventFileInput.value) {
                    eventFileInput.value.value = "";
                }

                successToast(translate("page.admin_edit_event.file_uploaded"));

                return listEventFiles();
            })
            .catch(handleError)
            .finally(() => {
                savingInProgress.value = false;
            });
    };

    const deleteEventFile = (id: number) => {
        deleteInProgress.value = id;
        return eventsApi
            .deleteEventFile(store.eventId, id)
            .then(() => {
                successToast(translate("page.admin_edit_event.file_deleted"));

                return listEventFiles();
            })
            .catch(handleError)
            .finally(() => {
                deleteInProgress.value = false;
            });
    };

    const listEventFiles = () => {
        return eventsApi
            .listEventFiles(store.eventId)
            .then(files => {
                eventFiles.value = files;
            })
            .catch(handleError);
    };

    onMounted(() => {
        listEventFiles().finally(() => {
            initDone.value = true;
        });
    });
</script>

<style scoped>
    .has-top-border {
        border-top: 1px dotted gray;
    }
</style>
